import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {Trans, useTranslation} from "react-i18next";
import {CustomerFactory} from "@connox/typescript-library";
import {getShopId} from "../lib/getShopId";
import styles from "./NewsletterRegistrationFooterBlock.module.scss";
import Spinner from "../utils/loader/Spinner";
import {RegistrationVoucher} from "../../helpers/registrationVoucher";
import {useCustomerSelector} from "../../store";
import {emailRegExp} from "../../helpers/emailRegExp";
import {cdnDomain} from "../../helpers/cnxDomains";

const NewsletterRegistrationFooterBlock: React.FC = () => {
  const {customer} = useCustomerSelector();
  const {t} = useTranslation();
  const [successState, setSuccessState] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: {errors},
    trigger,
  } = useForm<{email: string}>({
    mode: "onSubmit",
    defaultValues: {
      email: customer.email ?? "",
    },
  });

  const onSubmit = handleSubmit((data: {email: string}) => {
    setIsLoading(true);
    void trigger();
    void CustomerFactory.createOrUpdateCustomer(getShopId(), {email: data.email, newsletterFlag: true}).then(() => {
      setCustomerEmail(data.email);
      setSuccessState(true);
      setIsLoading(false);
    });
  });

  const resendEmail = () => setSuccessState(false);

  const Image = () => (
    <div className={styles.patternImage}>
      <picture>
        <source type="image/webp" srcSet="/media/Newsletter/Banner-Newsletter-Anmeldung-Mobil-Winter2024.webp" />
        <source type="image/jpg" srcSet="/media/Newsletter/Banner-Newsletter-Anmeldung-Mobil-Winter2024.jpg" />
        <img
          src={`${cdnDomain[getShopId()]}/media/Newsletter/Banner-Newsletter-Anmeldung-Mobil-Winter2024.jpg`}
          alt={t("newsletter.neverMissAnyDiscounts")}
        />
      </picture>
    </div>
  );

  const Advantages: React.FC = () => {
    if (!RegistrationVoucher.enabled) {
      return t("newsletter.registrationDiscountShortText");
    }

    return (
      <>
        <Trans
          i18nKey="newsletter.registrationWelcomeDiscount--amountFormatted"
          ns="translations"
          values={{
            amountFormatted: RegistrationVoucher.amountFormatted,
          }}
          components={{
            1: <sup />,
          }}
        />{" "}
        {t("newsletter.registrationEnjoyAdvantages")}
      </>
    );
  };

  return (
    <div className={styles.wrapper}>
      <Image />
      <div className={styles.patternContent}>
        {successState && (
          <React.Fragment>
            <h2>{t("newsletter.almostThere")}</h2>
            <p>
              <Trans
                i18nKey="newsletter.callToConfirmNewsletter"
                ns="translations"
                values={{customerEmail}}
                components={{
                  a: <strong />,
                }}
              />
            </p>
            <p>
              <Trans
                i18nKey="newsletter.resendMail"
                ns="translations"
                components={{
                  1: <span className={styles.resendLink} onClick={resendEmail} />,
                }}
              />
            </p>
          </React.Fragment>
        )}

        {!successState && (
          <React.Fragment>
            <h2>{t("newsletter.connoxNewsletter")}</h2>
            <p className={styles.subtitle} data-sth="superthing">
              <Advantages />
            </p>
            <form
              className={styles.patternForm}
              onSubmit={(evt: React.FormEvent<HTMLFormElement>) => void onSubmit(evt)}
            >
              <span className={styles.hasError}>{errors.email && errors.email.message}</span>
              <div className={styles.formRow}>
                <input
                  type="email"
                  className={`${styles.input} ${errors.email ? styles.invalid : ""}`}
                  placeholder={t("form.yourEmailAddress")}
                  {...register("email", {
                    required: true,
                    pattern: {
                      value: emailRegExp,
                      message: t("form.invalidEmailAddress"),
                    },
                  })}
                />
                <button className={styles.submitBtn} type="submit">
                  {isLoading ? <Spinner /> : <>{t("newsletter.subscribeNow")}</>}
                </button>
              </div>
            </form>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default NewsletterRegistrationFooterBlock;
